<script setup>
// Astra DS · DuotoneArt — el hueco de imagen del sistema.
//
// Figma: Pattern/DuotoneArt 637:287.
//
// «Gris por defecto, color solo en directo o al pasar — el color es
// información.» Una portada a color en un listado significa que eso está
// sonando ahora; si todo va a color, el color deja de decir nada.
//
// El token `art-*` queda debajo como color de carga, y sí cambia entre temas:
// en claro son grises cálidos, no negros. Un marcador negro sobre lino delata
// que el sistema de temas se rompió ahí.
import ButtonDs from "@/components/ds/ButtonDs.vue";
import CornerTag from "./CornerTag.vue";
import { computed } from "vue";
import { arte, esPlaceholder } from "@/services/placeholder";
import { i18n } from "@/i18n";

const props = defineProps({
  src: { type: String, default: "" },
  alt: { type: String, default: "" },

  // 🔴 **`prioritaria` para el arte que ya se ve al entrar.** Por defecto todo
  // el arte de la casa es `lazy`, que es lo correcto para una retícula; pero
  // una imagen que está en el primer pantallazo **no se puede aplazar**: el
  // navegador la pide tarde y es justo la que mide la velocidad de la página.
  //
  // Hoy la usa el cartel de la agenda, que ocupa media portada y pesa hasta
  // 4,5 MB en PNG (21.09.26). Con `lazy` se pedía después de todo lo demás.
  prioritaria: { type: Boolean, default: false },

  // Etiqueta de esquina. Vacía, no se pinta.
  tag: { type: String, default: "" },
  tagAnchor: { type: String, default: "bottom" },

  // Cuadrado rojo de play, centrado. Para pósters de vídeo.
  showPlay: { type: Boolean, default: false },
  playLabel: { type: String, default: () => i18n.global.t("piezas.emision.reproducir") },

  // Dónde se apoya el play. Centrado en el póster de vídeo; en la esquina
  // inferior izquierda en la puerta de TV, donde el centro del hueco lo ocupa
  // la propia imagen y el control estorbaría.
  playAnchor: {
    type: String,
    default: "center",
    validator: (v) => ["center", "bottom-left"].includes(v),
  },

  /**
   * El lado del play, en píxeles.
   *
   * 54 es lo que llevaba y lo que siguen midiendo las tarjetas y las puertas.
   * **El visor de Butaca pide 65**, que es la medida del play de la emisión en
   * el transporte: ahí el vídeo ocupa la primera vista entera y un cuadrado de
   * 54 en medio de 800 de alto se pierde (nodo `2173:25655`).
   *
   * ⚠️ El glifo va **proporcional al lado**, no fijo: 15 sobre 54 y 16 sobre 65,
   * que son los dos números que dan los nodos.
   */
  playLado: { type: Number, default: 54 },

  // En directo: recupera el color. Es la única razón, junto al hover, por la
  // que una imagen sale a color.
  live: { type: Boolean, default: false },

  // Si al pasar por encima vuelve el color. Cierto casi siempre: el hover
  // anuncia que eso lleva a algún sitio.
  //
  // Se apaga donde la imagen NO lleva a ninguna parte —la portada del
  // artículo, que ya es la página en la que estás—: ahí el color no tendría
  // nada que anunciar y se leería como un efecto. Es lo que hace el prototipo,
  // donde solo `.door`, `.lead` y `.card` recuperan color al pasar.
  hoverColor: { type: Boolean, default: true },

  // Si la imagen pasa por el gris del sistema.
  //
  // Cierto en todo el sistema —«el color es información», `DESIGN.md`—, y falso
  // en la única pieza que enseña un cartel de concierto: ver la excepción
  // escrita en `HeroAgenda`. No es una variante de estilo: es apagar un
  // procesado que sobre un cartel ajeno quita calidad en vez de darle
  // gramática.
  duotone: { type: Boolean, default: true },

  /**
   * Qué dice el gris de esta imagen.
   *
   * 🔴 **En el prototipo `Portada Astra v3` son dos grises distintos y dicen
   * cosas distintas**, así que aquí también:
   *
   *   `navegacion`  grayscale(1) contrast(1.1)   ← esto lleva a algún sitio;
   *                                                el color vuelve al pasar
   *   `emision`     grayscale(1) brightness(.75) ← esto está parado; el color
   *                                                vuelve cuando suena
   *
   * El de navegación levanta el contraste porque la imagen compite en una
   * retícula y tiene que leerse; el de emisión **apaga**, que es lo que se
   * quiere decir. En el prototipo lo pone el propio botón de reproducción, en
   * la misma función que apaga el punto rojo y congela el medidor: la imagen
   * es un indicador más de que la radio está parada.
   */
  tono: {
    type: String,
    default: "navegacion",
    validator: (v) => ["navegacion", "emision"].includes(v),
  },

  // Relación de aspecto. Cuadrado por defecto, como el nodo.
  ratio: { type: String, default: "1" },

  // A qué URL volver si `src` no carga. Ver `alFallar`.
  fallback: { type: String, default: "" },

  // Por dónde se recorta cuando la imagen no cabe.
  //
  // **Arriba por defecto**, no centrado: casi todo lo que entra en un hueco de
  // este sistema son fotos con gente —artistas, grupos, sesiones— y el motivo
  // vive en el tercio superior. Centrar el recorte les corta la cabeza. En una
  // imagen que sí cabe, o en un cuadrado dentro de un hueco cuadrado, da
  // igual: el valor solo actúa cuando sobra imagen.
  position: { type: String, default: "top" },
});

const emit = defineEmits(["play", "cargada"]);

// Sin foto llega el placeholder: la A de la marca, la del tema activo, sin
// gris y centrada. Ver `services/placeholder.js`.
const esMarca = computed(() => esPlaceholder(props.src));
const fuente = computed(() => arte(props.src));

// Respaldo para las URLs derivadas: si el tamaño pedido no existe —porque el
// original era más pequeño que él— se vuelve al archivo que dio la API. Sin
// esto, pedir un tamaño intermedio dejaría sin imagen justo a las piezas con
// la fotografía más pequeña.
function alFallar(e) {
  const img = e.target;
  if (!props.fallback || img.dataset.rescatada) return;
  img.dataset.rescatada = "1";
  img.src = props.fallback;
}

// Ancho por debajo del cual una imagen cargada se considera un marcador de
// «no disponible» y no una imagen de verdad.
const MINIMO = 121;

// 🔴 Hay fallos que NO disparan `onerror`.
//
// YouTube responde a una miniatura que no existe con **404 y una imagen gris
// de 120×90 en el cuerpo**. El navegador la considera una imagen válida, la
// pinta, y el respaldo de arriba no llega a ejecutarse: queda un hueco de 860
// con 120 píxeles estirados y ningún error en ninguna parte. Se descubrió en
// TV el 26.08 midiendo `naturalWidth`, no mirando.
//
// Así que además de escuchar el fallo se mira QUÉ ha cargado: si lo que llega
// es más pequeño que cualquier imagen real del sistema, se trata como si no
// hubiera llegado.
function alCargar(e) {
  const img = e.target;
  if (props.fallback && !img.dataset.rescatada && img.naturalWidth < MINIMO) {
    img.dataset.rescatada = "1";
    img.src = props.fallback;
    // Todavía no está: lo que ha llegado no valía y vamos a por el respaldo.
    return;
  }

  // 🔴 **Se avisa cuando la imagen ya está pintada**, no cuando se pide. Lo usa
  // quien enseña algo mientras tanto —hoy, el cartel de la agenda con su
  // indicador de carga—. Va después del rescate para no cantar victoria con la
  // miniatura de 120×90 que YouTube devuelve en vez de un 404.
  emit("cargada");
}
</script>

<template>
  <div
    class="duotone"
    :class="[
      `is-${tono}`,
      { 'is-live': live, 'is-flat': !hoverColor, 'is-raw': !duotone || esMarca },
    ]"
    :style="{ aspectRatio: ratio }"
  >
    <img
      v-if="src"
      class="duotone__img"
      :src="fuente"
      :alt="alt"
      :style="{ objectPosition: esMarca ? 'center' : position }"
      :loading="prioritaria ? 'eager' : 'lazy'"
      :fetchpriority="prioritaria ? 'high' : undefined"
      decoding="async"
      @error="alFallar"
      @load="alCargar"
    />

    <CornerTag v-if="tag" :anchor="tagAnchor">{{ tag }}</CornerTag>

    <!-- El play del arte: botón del sistema en forma de icono. El cuadrado rojo
         y dónde se ancla son de la imagen, no del botón. -->
    <ButtonDs
      v-if="showPlay"
      class="duotone__play"
      :class="`duotone__play--${playAnchor}`"
      variant="ghost"
      icono="play"
      :icono-tam="playLado >= 65 ? 16 : 15"
      :etiqueta="playLabel"
      :style="{ '--duotone-play-lado': `${playLado}px` }"
      @click="$emit('play')"
    />
  </div>
</template>

<style scoped lang="scss">
.duotone {
  position: relative;
  width: 100%;
  background: var(--color-art-mid);
  overflow: hidden;
}

// 🔴 **El filtro sale de una variable**, y así la pieza que contiene el arte
// puede encenderlo desde fuera: ver `scss/_interaccion.scss`. Antes el color
// solo volvía si el puntero caía sobre la imagen, de modo que una tarjeta con
// texto debajo se encendía o no según dónde estuviera el ratón.
.duotone__img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: var(--art-filtro, grayscale(1) contrast(1.15));
  transition: filter 0.15s linear;
}

// El gris que dice «esto está parado», no «esto lleva a algún sitio». Apaga en
// vez de levantar el contraste: `grayscale(1) brightness(.75)` del prototipo.
.duotone.is-emision .duotone__img {
  filter: var(--art-filtro, grayscale(1) brightness(0.75));
}

// Vuelve el color en directo, y también con el puntero sobre la propia imagen:
// esto último es el suelo, no el mecanismo. **Quien decide de verdad es la
// pieza**, encendiendo `--art-filtro` desde cualquiera de sus puntos —el
// titular, la fecha, el pie—; ver `scss/_interaccion.scss`. El suelo se queda
// para el arte que todavía no vive dentro de una pieza migrada.
.duotone:not(.is-flat):hover .duotone__img,
.duotone.is-live .duotone__img {
  filter: none;
}

// Y la imagen que no entra en el gris siquiera. No es «color al pasar»: es que
// aquí no hay nada que procesar, así que tampoco hay transición que animar.
.duotone.is-raw .duotone__img {
  filter: none;
  transition: none;
}

// Cuadrado, no círculo, y del mismo linaje que el punto de ASTRA.FM y el
// marcador de sección activa.
// El cuadrado rojo del play sobre una imagen. De la imagen son la medida, el
// anclaje y el rojo; del botón, todo lo demás.
//
// 🔴 **Aquí el hover sí rellena**, como el play de la emisión: es la misma
// acción y la misma forma en toda la casa. Es la excepción escrita a «el hover
// marca el filete» (18.09.26).
.duotone__play {
  position: absolute;
  width: var(--duotone-play-lado, 54px);
  height: var(--duotone-play-lado, 54px);
  border: 0;
  background: var(--color-bg-accent);
  color: var(--color-text-on-action);
  transition: background 0.12s linear;

  &--center {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  // 44px y apoyado en la esquina, como en la puerta. Más pequeño que el
  // centrado a propósito: aquí acompaña a la imagen, no la preside.
  &--bottom-left {
    bottom: 0;
    left: 0;
    width: 44px;
    height: 44px;
  }

  &:hover:not(:disabled) {
    // 🔴 **El glifo se queda en blanco.** El hover del `ghost` lo pasa al
    // acento, y sobre el rojo no se veía (Pablo, 01.10.26). Lleva `:not(:disabled)`
    // para pesar lo mismo que la regla de `ButtonDs` y ganarle.
    background: var(--color-action-primary-hover);
    color: var(--color-text-on-action);
  }
}

@media (prefers-reduced-motion: reduce) {
  .duotone__img {
    transition: none;
  }
}
</style>
